import $ from "jquery"; // Import jQuery
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import useRequiredFields from "../../../../../hooks/useRequiredFields";
import Http from "../../../../Common/Http";
import IsLogin from "../../../../Common/IsLogin";
import { LoaderComponent } from "../../../../Common/LoaderComponent";
import { ro } from "date-fns/locale";
import { ProgressSpinner } from "primereact/progressspinner";

const CreateGlazingGkTillSelection = () => {
  const { checkRequiredFields } = useRequiredFields();
  const navigate = useNavigate();
 
  const [inputs, setInputs] = useState({
    receipt_no: "",
    production_work_order_id: "",
    date: "",
    receive_date: "",
    copy_type: "",
    purchase_type: "",
    from_department: "",
    to_department: "",
    status: "requested",
    remarks: "",
  });

  const [list, setList] = useState([]);
  const [error, setError] = useState({});
  const [planningOptions, setPlanningOptions] = useState([]);
  const [role, setRole] = useState("");
  const [itemLoading, setItemLoading] = useState(false);
  const [searchProducts, setSearchProducts] = useState("");
  const [originalList, setOriginalList] = useState([]);
  // NEW: Store all items with user input across all pages
  const [allItemsWithInput, setAllItemsWithInput] = useState(new Map());
  const paginationRef = useRef({ current_page: 1, per_page: 15, total: 0, last_page: 1 });

  // Reference to store search timeout
  const searchTimeoutRef = useRef(null);

  const [colors] = useState([
    { id: 1, name: "White" },
    { id: 2, name: "Blue" },
    { id: 3, name: "Green" },
    { id: 4, name: "Red" },
    { id: 5, name: "Yellow" },
    { id: 6, name: "Brown" },
    { id: 7, name: "Black" },
    { id: 8, name: "Pink" },
    { id: 9, name: "Purple" },
    { id: 10, name: "Orange" },
  ]);
  const [loading, setLoading] = useState(false);

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const resRole = await Http.get("/staff-type-check");
      const fetchedRole = resRole.data;
      setRole(resRole.data);

      const shortPlanning = await Http.get("/short-planning");
      const short = shortPlanning.data;
      setPlanningOptions(short);
      if (short.length > 0) {
        const firstPlanning = short[0];
        const planning_id = shortPlanning.data.find(
          (item) => item.id == firstPlanning.id
        )?.planning_id;
        setInputs((prevInputs) => ({
          ...prevInputs,
          shortplan_id: firstPlanning.id, // Assuming the ID field for planning
          planning_id: planning_id,
        }));
        await handlePlanningChange(firstPlanning.id, planning_id);
      }

      const resReceiptNo = await Http.get(
        "/last-code-increment-by-one/production_finished_goods/receipt_no/P"
      );
      const receiptNo = resReceiptNo.data;

      setInputs((prevInputs) => ({
        ...prevInputs,
        receipt_no: receiptNo,
      }));
    } catch (e) {
      console.error("Error fetching data:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    $("body").addClass("collapse-menu");
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const handleInputChange = (e, index, available_qty = null) => {
    let { name, value } = e.target;
    const updatedList = [...list];

    // Handle string values for kiln_type, and numeric values for other fields
    let inputValue;
    if (name === "kiln_type") {
      inputValue = value; // Keep as string for kiln_type
    } else {
      inputValue = value === "" ? 0 : parseFloat(value);
    }
    
    const currentItem = updatedList[index];

    // Calculate total transfer_qty for the same product_id
    if (name === "transfer_qty") {
      const productId = currentItem.product_id;

      // Replace current row's value temporarily with the new one for sum calculation
      const tempList = [...updatedList];
      tempList[index][name] = inputValue;

      const transferQtySum = tempList
        .filter((item) => item.product_id === productId)
        .reduce((sum, item) => sum + (parseFloat(item.transfer_qty) || 0), 0);

      const totalOpeningQty = tempList
        .filter((item) => item.product_id === productId)
        .reduce((sum, item) => sum + (parseFloat(item.opening_qty) || 0), 0);
    }

    // Update the value
    updatedList[index][name] = inputValue;

    const closingBalance =
      (updatedList[index].opening_qty || 0) -
      (updatedList[index].return_qty || 0) -
      (updatedList[index].transfer_qty || 0) -
      (updatedList[index].after_production_loss_qty || 0) -
      (updatedList[index].before_production_loss_qty || 0) -
      (updatedList[index].total_loss_qty || 0);

    updatedList[index].closing_balance = closingBalance;

    setList(updatedList);

    const itemId = updatedList[index].id;
    setAllItemsWithInput((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(itemId, { ...updatedList[index] });
      return newMap;
    });

    setOriginalList((prevOriginalList) =>
      prevOriginalList.map((item, idx) =>
        item.id === updatedList[index].id ? { ...updatedList[index] } : item
      )
    );
  };

  const formSubmit = () => {
    checkRequiredFields();

    // NEW: Collect all items from all pages, not just current page
    const allItemsArray = Array.from(allItemsWithInput.values());

    const formData = {
      ...inputs,
      items: allItemsArray,
    };

    Http.post(`/production-transfer`, formData)
      .then((res) => {
        navigate("/transfer-art", {
          state: { message: res.data.message, type: "success" },
        });
      })
      .catch(function (e) {
        Swal.fire({
          title: "Error",
          text: e.response.data.errors.planning_id,
          icon: "error",
        });
      });
  };

  const cols = [{ field: "name", header: "Item" }];

  // Helper function to calculate shared available quantity
  const calculateSharedAvailableQty = (items) => {
    // Group items by production_work_order_id, shape_id, and product_id
    const groupedItems = {};

    items.forEach(item => {
      const key = `${item.production_work_order_id}-${item.shape_id}-${item.product_id}`;
      if (!groupedItems[key]) {
        groupedItems[key] = [];
      }
      groupedItems[key].push(item);
    });

    // Calculate shared available_qty for each group
    Object.keys(groupedItems).forEach(key => {
      const group = groupedItems[key];

      // Get the totals for this group (should be same for all items in group)
      const totalReceived = parseFloat(group[0]?.total_received_qty || 0);
      const totalTransfer = group.reduce((sum, item) => sum + parseFloat(item?.total_transfer_qty || 0), 0);
      const totalLoss = group.reduce((sum, item) => sum + parseFloat(item?.total_loss_qty || 0), 0);

      // Calculate shared available quantity
      const sharedAvailableQty = totalReceived - totalTransfer - totalLoss;

      // Apply the same available_qty to all items in this group
      group.forEach(item => {
        item.available_qty = sharedAvailableQty;
      });
    });

    return items;
  };


  const handlePlanningChange = async (shortplan_id, planning) => {
    if (!shortplan_id) return;

    const planning_id = planningOptions.find(
      (item) => item.id == shortplan_id
    )?.planning_id;
    setInputs((prevInputs) => ({
      ...prevInputs,
      shortplan_id: shortplan_id, // Update planning_id in inputs
      planning_id: planning ?? planning_id,
    }));

    try {
      // const response = await Http.get(
      //   `/show-current-active-planning/${shortplan_id}`
      // );
      const response = await Http.get(
        `/current-active-short-plan`
      );

      // Process collection of short plans from response.data
      let allPlanningItems = [];
      
      // Loop through each short plan in the collection
      response.data?.forEach((planData) => {
        const planningItems = planData.items
          .filter((item) => {
            if (role === "finishing") {
              return item?.product?.department_art_id === 2;
            }
            return true;
          })
          .map((item) => {
            const existingItem = allItemsWithInput.get(item.id);

            // Validate date condition if planning_for_glazing_and_jigger_item exists
            let isValidDate = true;
            if (item?.planning_for_glazing_and_jigger_item?.id) {
              const today = new Date().toISOString().split('T')[0]; // Get today's date in YYYY-MM-DD format
              const startDate = item?.planning_for_glazing_and_jigger_item?.start_date;
              const endDate = item?.planning_for_glazing_and_jigger_item?.end_date;
              
              // Check if today's date falls between start_date and end_date (inclusive)
              isValidDate = (startDate <= today && today <= endDate);
            }

            // Only create baseItem if date validation passes
            if (!isValidDate) {
              return null; // Skip this item if date validation fails
            }

            const baseItem = {
              id: item?.id,
              shortplan_id: shortplan_id,
          shortplan_item_id: item?.planning_for_glazing_and_jigger_item?.id,
              product_id: item?.product_id,
              product_name: item?.product?.name,
              production_work_order_id: item?.production_work_order_id,
              shape_id: item?.shape_id,
              qty: item?.planning_for_glazing_and_jigger_item?.required_qty,
              total_transfer: item?.total_transfer,
              shape: item?.shape?.name,
              production_work_order: item?.production_work_order?.receipt_no,
              company: item?.production_work_order?.company?.name,
              product_code: item?.product?.code,
              weight: item?.weight,
              load_qty: 0,
              before_production_loss_qty: 0,
              return_qty: 0,
              after_production_loss_qty: 0,
              opening_qty: item?.opening_qty,
              closing_balance: item?.qty,
              remarks_delivery: "",
              total_received_qty: item?.total_received_qty,
              total_transfer_qty: item?.total_transfer_qty,
              glaze_color_name:
                item?.production_work_order_item?.color?.colour_name,
              glaze_color: item?.production_work_order_item?.color?.id,
              total_loss_qty: item?.total_loss_qty,
              available_qty: parseFloat(item?.total_received_qty || 0) - parseFloat(item?.total_transfer_qty || 0),
              status: item?.production_work_order?.status,
              kiln_type: "",
            };

            return existingItem ? { ...baseItem, ...existingItem } : baseItem;
          })
          .filter(item => item !== null); // Filter out null items from date validation
        
        // Add items from this plan to the overall collection
        if (planningItems && planningItems.length > 0) {
          allPlanningItems = [...allPlanningItems, ...planningItems];
        }
      });

      // Apply shared available quantity calculation to all items
      allPlanningItems = calculateSharedAvailableQty(allPlanningItems);

      setList(allPlanningItems);
      setOriginalList(allPlanningItems);

      setAllItemsWithInput((prevMap) => {
        const newMap = new Map(prevMap);
        allPlanningItems.forEach((item) => {
          newMap.set(item.id, item);
        });
        return newMap;
      });
    } catch (error) {
      console.error("Error fetching products for planning:", error);
      Swal.fire(
        "Error!",
        "Failed to fetch products for the selected planning.",
        "error"
      );
    }
  };

  // Debounced search function
  const debouncedSearch = useCallback(async (searchValue, shortplan_id, page = 1) => {
    setLoading(true);
    
    if (searchValue.trim() === "") {
      setList(originalList);
      setLoading(false);
      return;
    }

    if (["finishing", "bk", "glazing", "gk"].includes(role)) {
      try {
        const response = await Http.post(`/search-products-for-jigger`, {
          search: searchValue,
          shortplan_id,
        });

        let planningItems = response.data.items
          .filter((item) => {
            if (role === "finishing") {
              return item?.product?.department_art_id === 2;
            }
            return true;
          })
          .map((item) => {
            const existingItem = allItemsWithInput.get(item.id);

            const baseItem = {
              id: item?.id,
              shortplan_id: inputs.shortplan_id,
              shortplan_item_id: item?.planning_for_glazing_and_jigger_item?.id,
    
              product_id: item?.product_id,
              product_name: item?.product?.name,
              production_work_order_id: item?.production_work_order_id,
              shape_id: item?.shape_id,
              qty: item?.planning_for_glazing_and_jigger_item?.required_qty,
              total_transfer: item?.total_transfer,
              shape: item?.shape?.name,
              production_work_order: item?.production_work_order?.receipt_no,
              company: item?.production_work_order?.company?.name,
              product_code: item?.product?.code,
              weight: item?.weight,
              load_qty: 0,
              before_production_loss_qty: 0,
              return_qty: 0,
              after_production_loss_qty: 0,
              opening_qty: item?.opening_qty,
              closing_balance: item?.qty,
              remarks_delivery: "",
              total_received_qty: item?.total_received_qty,
              total_transfer_qty: item?.total_transfer_qty,
              glaze_color_name:
                item?.production_work_order_item?.color?.colour_name,
              total_loss_qty: item?.total_loss_qty,
              kiln_type: "",
              status: item?.production_work_order?.status,
              available_qty:
                parseFloat(item?.total_received_qty) -
                parseFloat(item?.total_transfer_qty),
            };

            return existingItem ? { ...baseItem, ...existingItem } : baseItem;
          });
        planningItems = calculateSharedAvailableQty(planningItems);

        setList(planningItems);
        // setPagination(response.data.pagination);

        setAllItemsWithInput((prevMap) => {
          const newMap = new Map(prevMap);
          planningItems.forEach((item) => {
            newMap.set(item.id, item);
          });
          return newMap;
        });
      } catch (error) {
        console.error("Search error:", error);
        Swal.fire("Error!", "Failed to search products.", "error");
      } finally {
        setLoading(false);
      }
    } else {
      const searchProduct = originalList.filter((item) =>
        item?.product_name?.toLowerCase()?.includes(searchValue?.toLowerCase())
      );
      setList(searchProduct);
      setLoading(false);
    }
  }, [role, allItemsWithInput, originalList, setList, setAllItemsWithInput, setLoading]);

  // Handle search with debouncing
  const handleSearchProduct = (e, page = 1) => {
    const searchValue = e.target.value;
    setSearchProducts(searchValue);
    const shortplan_id = inputs.shortplan_id;
    
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set loading state to show user that search is in progress
    if (searchValue.trim() !== "") {
      setLoading(true);
    }
    
    // Set a new timeout
    searchTimeoutRef.current = setTimeout(() => {
      debouncedSearch(searchValue, shortplan_id, page);
    }, 2000); // 500ms delay
  };

  return (
    <div>
      <IsLogin></IsLogin>
      {/* <LoaderComponent /> */}
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Production and Deliver</h5>
        </div>
        <div className="am-pagebody  row d-flex justify-content-evenly">
          <div className="card pd-20 pd-sm-40 col-11">
            <h6 className="card-body-title mg-sm-b-30">
              Production and Delivery
            </h6>

            <div
              className="row my-1 pt-3 mb-2 fixed-header"
              style={{
                position: "sticky",
                top: "12%",
                width: "102.1%",
                marginBottom: "10px",
                zIndex: "1002",
                backgroundColor: "white",
                transition: "all 0.3s ease-in-out",
                transform: "translateY(0)",
              }}
            >
              <div className="col-lg">
                <label>Receipt No</label>
                <input
                  type="text"
                  className="form-control"
                  name="receipt_no"
                  value={inputs.receipt_no}
                  disabled
                />
              </div>

              {/* <div className="col-lg my-2">
                <label>Short Planning</label>
                <select
                  className="form-control"
                  name="shortplan_id"
                  value={inputs.shortplan_id || ""} // Bind to inputs
                  onChange={(e) => handlePlanningChange(e.target.value)}
                >
                  <option value="" selected disabled>Select Short Planning</option>
                  {planningOptions.map((planning) => (
                    <option key={planning.id} value={planning.id}>
                      {planning.receipt_no}
                    </option>
                  ))}
                </select>
              </div> */}
              <div className="col-lg">
                <label>
                  Shift <i className="fa fa-star required"></i>
                </label>
                <select
                  className="form-control"
                  name="shift"
                  value={inputs.shift}
                  onChange={handleChange}
                  data-required
                >
                  <option value="">Select Shift</option>
                  <option value="a">A</option>
                  <option value="b">B</option>
                  <option value="c">C</option>
                  <option value="general">General</option>
                </select>
              </div>
            </div>

            <div
              className="row fixed-header pb-1"
              style={{
                position: "sticky",
                top: "22%",
                width: "102.1%",
                marginBottom: "10px",
                zIndex: "1000",
                backgroundColor: "white",
                transition: "all 0.3s ease-in-out",
                transform: "translateY(0)",
              }}
            >
              <div className="col-lg my-2 ">
                <label>Search Product</label>
                <input
                  type="search"
                  className="form-control"
                  name="search_product"
                  placeholder="Search Product"
                  onChange={(e) => handleSearchProduct(e)}
                />
              </div>

              <div className="col-lg my-2 ">
                <label>Remarks</label>
                <textarea
                  className="form-control"
                  name="remarks"
                  value={inputs.remarks}
                  onChange={handleChange}
                />
              </div>
              <div className="col-lg mt-5">
                <button className="btn btn-primary" onClick={formSubmit}>
                  Submit
                </button>
              </div>
            </div>

            {/* Item List */}
            {loading ? (
              <ProgressSpinner />
            ) : (
              <>
                <div className="row mt-3">
                  <div className="card-solid">
                    <div className="card-header with-border text-center">
                      <h5 className="card-title mb-0">
                        Items in the Production
                      </h5>
                    </div>
                  </div>
                  <div className="col-lg">
                    <table className="table table-sm" style={{ borderCollapse: 'collapse', width: '100%' }}>
                      <thead
                        className="fixed-header"
                        style={{
                          position: "sticky",
                          top: "35%",
                          width: "104%",
                          marginBottom: "10px",
                          zIndex: "1000",
                          backgroundColor: "white",
                          transition: "all 0.3s ease-in-out",
                          transform: "translateY(0)",
                        }}
                      >
                        <tr>
                          <th>Product</th>
                          <th>Shape</th>
                          <th>Company</th>
                          <th>Planning Qty</th>
                          {role === "bk" && <th>Biscuit Weight</th>}
                          <th>Opening Balance</th>
                          {role !== "bk" && role !== "gk" && <th>Transfer</th>}
                          <th>Transfered</th>
                          <th>Available</th>
                          <th>Received</th>
                          {(role === "bk" ||
                            role === "gk" ||
                            role === "glazing") && (
                              <>
                                {role !== "glazing" && <th>Load</th>}
                                {role === "bk" && <th>Loss</th>}
                                {role === "gk" && <th>Load Loss</th>}
                                {role === "glazing" && (
                                  <>
                                    <th>Kiln Type</th>
                                    <th>Before Glaze Loss</th>
                                    <th>After Glaze Loss</th>
                                  </>
                                )}
                                {role !== "glazing" && (
                                  <>
                                    <th>Unload</th>
                                    <th>Unload Loss</th>
                                  </>
                                )}
                                <th>Total Loss Qty</th>
                              </>
                            )}
                          {role === "finishing" && <th>Loss </th>}
                          <th>Closing Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {list.length < 1 ? (
                          <tr>
                            <td colSpan={13} className="text-center">
                              Please add products
                            </td>
                          </tr>
                        ) : (
                          list.map((item, index) => (
                            <tr
                              key={index}
                              className={
                                Number(item?.total_transfer_qty) >
                                  Number(item?.qty)
                                  ? "bg-warning text-white"
                                  : Number(item?.total_transfer_qty) ==
                                    Number(item?.qty)
                                    ? "bg-success  text-white"
                                    : ""
                              }
                              style={{
                                fontStyle: item?.status === 'on_test' ? 'italic' : undefined,
                                fontWeight: item?.status === 'on_test' ? '700' : undefined,
                                border: item?.status === 'on_test' ? '2px solid rgb(0, 89, 119)' : undefined,
                              }}
                            >
                              <td
                                className={
                                  Number(item?.total_transfer_qty) >
                                    Number(item?.qty)
                                    ? "text-white"
                                    : Number(item?.total_transfer_qty) ==
                                      Number(item?.qty)
                                      ? "text-white"
                                      : ""
                                }
                              >
                                {item.product_name} <br />
                                <br />
                                {(role === "glazing" || role === "gk") && (
                                  <b>
                                    Glaze: {item?.glaze_color_name || "N/A"}
                                  </b>
                                )}
                              </td>
                              <td
                                className={
                                  Number(item?.total_transfer_qty) >
                                    Number(item?.qty)
                                    ? "text-white"
                                    : Number(item?.total_transfer_qty) ==
                                      Number(item?.qty)
                                      ? "text-white"
                                      : ""
                                }
                              >
                                {" "}
                                {item.shape}
                              </td>
                              <td
                                className={
                                  Number(item?.total_transfer_qty) >
                                    Number(item?.qty)
                                    ? "text-white"
                                    : Number(item?.total_transfer_qty) ==
                                      Number(item?.qty)
                                      ? "text-white"
                                      : ""
                                }
                              >
                                {item.company}
                                <strong>
                                  #{item.production_work_order}
                                </strong>{" "}
                              </td>
                              <td
                                className={
                                  Number(item?.total_transfer_qty) >
                                    Number(item?.qty)
                                    ? "text-white"
                                    : Number(item?.total_transfer_qty) ==
                                      Number(item?.qty)
                                      ? "text-white"
                                      : ""
                                }
                              >
                                {item.qty}
                              </td>
                              {role === "bk" && (
                                <td
                                  className={
                                    Number(item?.total_transfer_qty) >
                                      Number(item?.qty)
                                      ? "text-white"
                                      : Number(item?.total_transfer_qty) ==
                                        Number(item?.qty)
                                        ? "text-white"
                                        : ""
                                  }
                                >
                                  <input
                                    type="text"
                                    onInput={(e) =>
                                    (e.target.value = e.target.value.replace(
                                      /\D/g,
                                      ""
                                    ))
                                    }
                                    className="form-control"
                                    name="biscuit_weight"
                                    value={item.biscuit_weight || ""}
                                    onChange={(e) =>
                                      handleInputChange(e, index)
                                    }
                                  />
                                </td>
                              )}
                              <td
                                className={
                                  Number(item?.total_transfer_qty) >
                                    Number(item?.qty)
                                    ? "text-white"
                                    : Number(item?.total_transfer_qty) ==
                                      Number(item?.qty)
                                      ? "text-white"
                                      : ""
                                }
                              >
                                {item.opening_qty}
                              </td>
                              {role !== "bk" && role !== "gk" && (
                                <td
                                  className={
                                    Number(item?.total_transfer_qty) >
                                      Number(item?.qty)
                                      ? "text-white"
                                      : Number(item?.total_transfer_qty) ==
                                        Number(item?.qty)
                                        ? "text-white"
                                        : ""
                                  }
                                >
                                  <input
                                    type="text"
                                    onInput={(e) =>
                                    (e.target.value = e.target.value.replace(
                                      /\D/g,
                                      ""
                                    ))
                                    }
                                    className="form-control"
                                    name="transfer_qty"
                                    value={list[index].transfer_qty || 0}
                                    onChange={(e) =>
                                      handleInputChange(e, index)
                                    }
                                  />
                                </td>
                              )}
                              <td
                                className={
                                  Number(item?.total_transfer_qty) >
                                    Number(item?.qty)
                                    ? "text-white"
                                    : Number(item?.total_transfer_qty) ==
                                      Number(item?.qty)
                                      ? "text-white"
                                      : ""
                                }
                              >
                                {item.total_transfer_qty}
                              </td>
                              <td className={
                                Number(item?.total_transfer_qty) >
                                  Number(item?.qty)
                                  ? "text-white"
                                  : Number(item?.total_transfer_qty) ==
                                    Number(item?.qty)
                                    ? "text-white"
                                    : ""
                              }>
                                {item.available_qty}
                              </td>
                              <td
                                className={
                                  Number(item?.total_transfer_qty) >
                                    Number(item?.qty)
                                    ? "text-white"
                                    : Number(item?.total_transfer_qty) ==
                                      Number(item?.qty)
                                      ? "text-white"
                                      : ""
                                }
                              >
                                {item.total_received_qty}
                              </td>

                              {(role === "bk" ||
                                role === "gk" ||
                                role === "glazing") && (
                                  <>
                                    {role !== "glazing" && (
                                      <td
                                        className={
                                          Number(item?.total_transfer_qty) >
                                            Number(item?.qty)
                                            ? "text-white"
                                            : Number(item?.total_transfer_qty) ==
                                              Number(item?.qty)
                                              ? "text-white"
                                              : ""
                                        }
                                      >
                                        <input
                                          type="text"
                                          onInput={(e) =>
                                          (e.target.value =
                                            e.target.value.replace(/\D/g, ""))
                                          }
                                          className="form-control"
                                          name="load_qty"
                                          value={item.load_qty || ""}
                                          onChange={(e) =>
                                            handleInputChange(
                                              e,
                                              index,
                                              item.total_received_qty -
                                              item.total_transfer_qty -
                                              item.total_loss_qty
                                            )
                                          }
                                        />
                                      </td>
                                    )}

                                    {role === "bk" && (
                                      <td
                                        className={
                                          Number(item?.total_transfer_qty) >
                                            Number(item?.qty)
                                            ? "text-white"
                                            : Number(item?.total_transfer_qty) ==
                                              Number(item?.qty)
                                              ? "text-white"
                                              : ""
                                        }
                                      >
                                        <input
                                          type="text"
                                          onInput={(e) =>
                                          (e.target.value =
                                            e.target.value.replace(/\D/g, ""))
                                          }
                                          className="form-control"
                                          name="return_qty"
                                          value={item.return_qty}
                                          onChange={(e) =>
                                            handleInputChange(e, index)
                                          }
                                        />
                                      </td>
                                    )}
                                    {role === "gk" && (
                                      <td
                                        className={
                                          Number(item?.total_transfer_qty) >
                                            Number(item?.qty)
                                            ? "text-white"
                                            : Number(item?.total_transfer_qty) ==
                                              Number(item?.qty)
                                              ? "text-white"
                                              : ""
                                        }
                                      >
                                        <input
                                          type="text"
                                          onInput={(e) =>
                                          (e.target.value =
                                            e.target.value.replace(/\D/g, ""))
                                          }
                                          className="form-control"
                                          name="before_production_loss_qty"
                                          value={
                                            item.before_production_loss_qty || ""
                                          }
                                          onChange={(e) =>
                                            handleInputChange(
                                              e,
                                              index,
                                              item.total_received_qty -
                                              item.total_transfer_qty -
                                              item.total_loss_qty
                                            )
                                          }
                                        />
                                      </td>
                                    )}
                                    {role === "glazing" && (
                                      <>
                                      <td
                                          className={
                                            Number(item?.total_transfer_qty) >
                                              Number(item?.qty)
                                              ? "text-white"
                                              : Number(item?.total_transfer_qty) ==
                                                Number(item?.qty)
                                                ? "text-white"
                                                : ""
                                          }
                                        >
                                          <select
                                            className={`form-control ${Number(item.transfer_qty) > 0 ? 'is-invalid' : ''}`}
                                            name="kiln_type"
                                            value={item.kiln_type || ""}
                                            onChange={(e) =>
                                              handleInputChange(e, index)
                                            }
                                            data-required={Number(item.transfer_qty) > 0 ? "true" : "false"}
                                            required={Number(item.transfer_qty) > 0}
                                          >
                                            <option value="">Select Kiln Type</option>
                                            <option value="shuttle_kiln">Shuttle Kiln</option>
                                            <option value="tunnel_kiln">Tunnel Kiln</option>
                                          </select>
                                          {/* {Number(item.transfer_qty) > 0 && !item.kiln_type && (
                                            <div className="invalid-feedback">
                                              Kiln type is required when transfer quantity is specified
                                            </div>
                                          )} */}
                                        </td>
                                      <td
                                        className={
                                          Number(item?.total_transfer_qty) >
                                            Number(item?.qty)
                                            ? "text-white"
                                            : Number(item?.total_transfer_qty) ==
                                              Number(item?.qty)
                                              ? "text-white"
                                              : ""
                                        }
                                      >
                                        <input
                                          type="text"
                                          onInput={(e) =>
                                          (e.target.value =
                                            e.target.value.replace(/\D/g, ""))
                                          }
                                          className="form-control"
                                          name="before_production_loss_qty"
                                          value={
                                            item.before_production_loss_qty || ""
                                          }
                                          onChange={(e) =>
                                            handleInputChange(e, index)
                                          }
                                        />
                                      </td>
                                      </>
                                    )}
                                    {role !== "glazing" && (
                                      <td
                                        className={
                                          Number(item?.total_transfer_qty) >
                                            Number(item?.qty)
                                            ? "text-white"
                                            : Number(item?.total_transfer_qty) ==
                                              Number(item?.qty)
                                              ? "text-white"
                                              : ""
                                        }
                                      >
                                        <input
                                          type="text"
                                          onInput={(e) =>
                                          (e.target.value =
                                            e.target.value.replace(/\D/g, ""))
                                          }
                                          className="form-control"
                                          name="transfer_qty"
                                          value={list[index].transfer_qty || ""}
                                          onChange={(e) =>
                                            handleInputChange(
                                              e,
                                              index,
                                              item.total_received_qty -
                                              item.total_transfer_qty -
                                              item.total_loss_qty
                                            )
                                          }
                                        />
                                      </td>
                                    )}
                                    <td
                                      className={
                                        Number(item?.total_transfer_qty) >
                                          Number(item?.qty)
                                          ? "text-white"
                                          : Number(item?.total_transfer_qty) ==
                                            Number(item?.qty)
                                            ? "text-white"
                                            : ""
                                      }
                                    >
                                      <input
                                        type="text"
                                        onInput={(e) =>
                                        (e.target.value =
                                          e.target.value.replace(/\D/g, ""))
                                        }
                                        className="form-control"
                                        name="after_production_loss_qty"
                                        value={
                                          item.after_production_loss_qty || ""
                                        }
                                        onChange={(e) =>
                                          handleInputChange(
                                            e,
                                            index,
                                            item.total_received_qty -
                                            item.total_transfer_qty -
                                            item.total_loss_qty
                                          )
                                        }
                                      />
                                    </td>
                                    <td className={
                                      Number(item?.total_transfer_qty) >
                                        Number(item?.qty)
                                        ? "text-white"
                                        : Number(item?.total_transfer_qty) ==
                                          Number(item?.qty)
                                          ? "text-white"
                                          : ""
                                    }>{item.total_loss_qty || 0}</td>
                                  </>
                                )}
                              {role === "finishing" && (
                                <td
                                  className={
                                    Number(item?.total_transfer_qty) >
                                      Number(item?.qty)
                                      ? "text-white"
                                      : Number(item?.total_transfer_qty) ==
                                        Number(item?.qty)
                                        ? "text-white"
                                        : ""
                                  }
                                >
                                  <input
                                    type="text"
                                    onInput={(e) =>
                                    (e.target.value = e.target.value.replace(
                                      /\D/g,
                                      ""
                                    ))
                                    }
                                    className="form-control"
                                    name="return_qty"
                                    value={list[index].return_qty || ""}
                                    onChange={(e) =>
                                      handleInputChange(e, index)
                                    }
                                  />
                                </td>
                              )}

                              <td
                                className={
                                  Number(item?.total_transfer_qty) >
                                    Number(item?.qty)
                                    ? "text-white"
                                    : Number(item?.total_transfer_qty) ==
                                      Number(item?.qty)
                                      ? "text-white"
                                      : ""
                                }
                              >
                                {(
                                  Number(item.opening_qty || 0) -
                                  Number(item.return_qty || 0) -
                                  Number(item.transfer_qty || 0) -
                                  Number(item.after_production_loss_qty || 0) -
                                  Number(item.before_production_loss_qty || 0)
                                ).toFixed(2)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateGlazingGkTillSelection;
